// Updated MainLayout.js with smart auto-collapse
import React, { useState, useCallback, useRef, useEffect, memo, useMemo } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import Sidebar from "../../shared/navigation/SideBar";
import TopBar from "../../shared/navigation/TopBar";
import GlobalRunProgress from "../components/GlobalRunProgress";
import { useTheme } from "../../styles/ThemeContext";
import { useUser, useUserName, useUserSurname } from "../../features/auth/hooks/useUser";
import { ReactComponent as BellIcon } from "../../assets/icons/commonIcons/BellIcon.svg";
import NotificationPanel from "../notifications/NotificationPanel.jsx";
import { useNotifications } from "../../features/organization/hooks/useNotifications.js";
import { useAuth } from "../../features/auth/hooks/useAuth";
import { LogOut } from "lucide-react";
import { useProjects } from "../middleware/hooks/ProjectContext";
import { useAutomationIsLoading } from "../../store/automation/automationStore";
import { useLayoutStore } from "../../store/layout/layoutStore";

const getGreeting = () => {
    const h = new Date().getHours();
    if (h >= 5 && h < 12) return 'Good morning';
    if (h >= 12 && h < 18) return 'Good afternoon';
    return 'Good evening';
};

const GOVERNANCE = { label: 'Governance', path: null, nonClickable: true };
const ORGANIZATION = { label: 'Organization', path: '/organization-settings', nonClickable: true };

const BREADCRUMB_MAP = [
    { match: /^\/dashboard/, crumbs: [{ label: 'Dashboard', path: '/dashboard' }] },
    { match: /^\/capabilities/, crumbs: [GOVERNANCE, { label: 'Quality Intelligence Suite', path: '/data-analyzer-home' }, { label: 'All Capabilities', path: '/capabilities' }] },
    { match: /^\/data-analyzer-chat/, crumbs: [GOVERNANCE, { label: 'Quality Intelligence Suite', path: '/capabilities' }, { label: 'Analysis', path: '/data-analyzer-chat' }] },
    { match: /^\/data-analyzer-home/, crumbs: [GOVERNANCE, { label: 'Quality Intelligence Suite', path: '/data-analyzer-home' }] },
    { match: /^\/chat-history/, crumbs: [GOVERNANCE, { label: 'Quality Intelligence Suite', path: '/capabilities' }, { label: 'QIS Repository', path: '/chat-history' }] },
    { match: /^\/automation-script/, crumbs: [GOVERNANCE, { label: 'Engineering Engine', path: '/projects' }, { label: 'Business Process Repository', path: '/projects' }, { label: 'Automation Scripts', path: null, nonClickable: true }] },
    { match: /^\/projects|\/project-chat|\/test-suite/, crumbs: [GOVERNANCE, { label: 'Engineering Engine', path: '/projects' }] },
    { match: /^\/organization-settings/, crumbs: [ORGANIZATION, { label: 'Settings', path: '/organization-settings' }] },
    { match: /^\/organization-roles/, crumbs: [ORGANIZATION, { label: 'Roles & Permissions', path: '/organization-roles-and-permissions' }] },
    { match: /^\/organization-members/, crumbs: [ORGANIZATION, { label: 'Members', path: '/organization-members' }] },
    { match: /^\/token-usage/, crumbs: [ORGANIZATION, { label: 'Monitoring', path: '/token-usage' }] },
];

const BreadcrumbChevron = ({ isBeforeCurrent }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
        <path d="M6 3.33325L10.6667 7.99992L6 12.6666"
            stroke={isBeforeCurrent ? 'var(--primary-color, #9404C3)' : 'var(--text-muted, #999)'}
            strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const Breadcrumbs = ({ pathname, onNavigate, projectName }) => {
    const entry = BREADCRUMB_MAP.find(e => e.match.test(pathname));
    if (!entry) return null;

    // Append project name crumb when on a project detail page
    const projectMatch = pathname.match(/^\/projects\/([^/]+)/);
    const crumbs = (projectMatch && projectName)
        ? [...entry.crumbs, { label: projectName, path: pathname }]
        : entry.crumbs;

    return (
        <div className="breadcrumb-row" style={{
            display: 'flex', alignItems: 'center',
            padding: '6px 24px 2px 24px', flexShrink: 0,
            background: 'var(--background-color, #EEE)'
        }}>
            {crumbs.map((crumb, i) => {
                const isLast = i === crumbs.length - 1;
                const isClickable = !isLast && !crumb.nonClickable;
                return (
                    <React.Fragment key={`${crumb.label}-${i}`}>
                        <span
                            onClick={() => isClickable && onNavigate(crumb.path)}
                            className={`breadcrumb-crumb ${isLast ? 'breadcrumb-crumb--last' : ''} ${crumb.nonClickable ? 'breadcrumb-crumb--static' : ''}`}
                            style={crumb.nonClickable ? { cursor: 'default' } : undefined}
                        >
                            {crumb.label}
                        </span>
                        {!isLast && <BreadcrumbChevron isBeforeCurrent={i === crumbs.length - 2} />}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

const MainLayout = memo(() => {
    const location = useLocation();
    const navigate = useNavigate();
    const contentRef = useRef(null);
    const { isDarkMode, toggleDarkMode } = useTheme();
    const { userData } = useUser();
    const firstName = useUserName();
    const surname = useUserSurname();

    const isCapabilitiesPage = location.pathname === '/capabilities';
    // Hide breadcrumbs while an automation script is still loading
    const automationLoading = useAutomationIsLoading();
    const hideBreadcrumbs = automationLoading && location.pathname.startsWith('/automation-script');
    const greeting = useMemo(() => getGreeting(), []);
    const fullName = [firstName, surname].filter(Boolean).join(' ') || userData?.name || '';
    const userRole = userData?.roleName || userData?.role || '';
    const orgName = userData?.currentOrganisationName || userData?.organizations?.[0]?.name || '';
    const userId = userData?.userId || userData?.id;
    const initials = [firstName?.[0], surname?.[0]].filter(Boolean).join('').toUpperCase() || (userData?.name?.[0] || '?').toUpperCase();

    const [isNotifOpen, setIsNotifOpen] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const profileDropdownRef = useRef(null);
    const { notifications, unreadCount, handleMarkAsRead, handleMarkAllAsRead } = useNotifications(userId);
    const { logout } = useAuth();
    const { projects } = useProjects();
    const projectIdMatch = location.pathname.match(/^\/projects\/([^/]+)/);
    const currentProject = projectIdMatch
        ? projects.find(p => p.id === projectIdMatch[1])
        : null;
    const currentProjectName = currentProject?.displayName || currentProject?.name || null;

    useEffect(() => {
        if (!isProfileOpen) return;
        const handleClick = (e) => {
            if (profileDropdownRef.current && !profileDropdownRef.current.contains(e.target)) {
                setIsProfileOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [isProfileOpen]);
    
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [sidebarWidth, setSidebarWidth] = useState(260); // Match your CSS default
    const [isResizing, setIsResizing] = useState(false);
    const [autoCollapsed, setAutoCollapsed] = useState(false); // Track auto-collapse state
    const forceSidebarCollapsed = useLayoutStore((state) => state.forceSidebarCollapsed); // e.g. AI agent panel needs the space
    const sidebarRef = useRef(null);

        // ADD THIS EFFECT - Reset scroll on route change
    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
    }, [location.pathname]); // Runs whenever route changes

    
    // Minimum and maximum sidebar widths
    const MIN_WIDTH = 250;
    const MAX_WIDTH = 600;
    const COLLAPSE_THRESHOLD = 150; // When dragging below this width, auto-collapse

    // Handle mouse down on resize handle
    const handleMouseDown = useCallback((e) => {
        setIsResizing(true);
        e.preventDefault();
    }, []);

    // Handle mouse move during resize with smart collapse (RAF-throttled for 60fps)
    const rafRef = useRef(null);
    const handleMouseMove = useCallback((e) => {
        if (!isResizing) return;

        // Throttle to animation frames for smooth 60fps without unnecessary re-renders
        if (rafRef.current) return;

        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null;
            const newWidth = e.clientX;

            // Check if we should auto-collapse
            if (newWidth < COLLAPSE_THRESHOLD) {
                if (!isCollapsed && !autoCollapsed) {
                    setAutoCollapsed(true);
                    setIsCollapsed(true);
                }
            } else if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
                // Normal resize range - expand if auto-collapsed
                if (autoCollapsed || isCollapsed) {
                    setAutoCollapsed(false);
                    setIsCollapsed(false);
                }
                setSidebarWidth(newWidth);
            } else if (newWidth >= COLLAPSE_THRESHOLD && newWidth < MIN_WIDTH && (autoCollapsed || isCollapsed)) {
                // User dragged out of collapse zone but not to minimum - expand to minimum
                setAutoCollapsed(false);
                setIsCollapsed(false);
                setSidebarWidth(MIN_WIDTH);
            }
        });
    }, [isResizing, autoCollapsed, isCollapsed]);

    // Handle mouse up to stop resizing
    const handleMouseUp = useCallback(() => {
        setIsResizing(false);
    }, []);

    // Enhanced collapse toggle that respects auto-collapse
    const handleToggleCollapse = useCallback((collapsed) => {
        setIsCollapsed(collapsed);
        if (!collapsed) {
            setAutoCollapsed(false); // Clear auto-collapse when manually expanding
        }
    }, []);

    // Responsive behavior - auto-collapse on small screens
    const handleResponsiveResize = useCallback(() => {
        const screenWidth = window.innerWidth;

        if (screenWidth <= 768) { // Mobile breakpoint
            if (!autoCollapsed && !isCollapsed) {
                setAutoCollapsed(true);
                setIsCollapsed(true);
            }
        } else if (screenWidth <= 1024) { // Tablet breakpoint
            // Force to minimum width on tablets if expanded
            if (!isCollapsed && sidebarWidth > MIN_WIDTH) {
                setSidebarWidth(MIN_WIDTH);
            }
        } else {
            // Large screen — restore sidebar if it was auto-collapsed (e.g. moved between monitors)
            if (autoCollapsed) {
                setAutoCollapsed(false);
                setIsCollapsed(false);
            }
        }
    }, [isCollapsed, autoCollapsed, sidebarWidth]);

    // Add and remove event listeners for dragging
    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            // Cancel resize if the OS captures the mouse (e.g. dragging the app window)
            window.addEventListener('blur', handleMouseUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        } else {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('blur', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('blur', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);

    // Add debounced responsive resize listener (150ms debounce)
    const resizeTimerRef = useRef(null);
    useEffect(() => {
        handleResponsiveResize(); // Run on mount

        const debouncedResize = () => {
            if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
            resizeTimerRef.current = setTimeout(handleResponsiveResize, 150);
        };

        window.addEventListener('resize', debouncedResize);

        return () => {
            window.removeEventListener('resize', debouncedResize);
            if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
        };
    }, [handleResponsiveResize]);

    const isEffectivelyCollapsed = isCollapsed || autoCollapsed || forceSidebarCollapsed;

    // Memoize style objects to avoid recreating on every render
    const handleStyle = useMemo(() => ({
        width: isEffectivelyCollapsed ? '12px' : '6px',
        opacity: isEffectivelyCollapsed ? 0.7 : 0.3
    }), [isEffectivelyCollapsed]);

    // Other features (e.g. AnalyzerChat) position elements relative to the
    // sidebar's actual width via this variable — keep it in sync even though
    // the sidebar's own animation no longer reads it.
    const containerStyle = useMemo(() => ({
        '--sidebar-width': isEffectivelyCollapsed ? '100px' : '260px'
    }), [isEffectivelyCollapsed]);

    return (
        <div className="screen-container" style={containerStyle}>
            {/* TopBar hidden — replaced by new top nav below */}
            <div className="topbar-container" style={{ display: 'none' }}>
                <TopBar />
            </div>

            {/* Sidebar + Content sit below the topbar */}
            <div className={`layout-body ${isEffectivelyCollapsed ? 'sidebar-is-collapsed' : ''}`}>
                <div
                    ref={sidebarRef}
                    className={`sidebar-container ${isEffectivelyCollapsed ? 'collapsed' : 'resizable'}`}
                >
                    <Sidebar
                        isCollapsed={isEffectivelyCollapsed}
                        onToggleCollapse={handleToggleCollapse}
                    />

                    {/* Always show resize handle - even when collapsed for easy expansion */}
                    <div
                        className={`sidebar-resize-handle ${isResizing ? 'resizing' : ''}`}
                        onMouseDown={handleMouseDown}
                        style={handleStyle}
                    />
                </div>

                <div
                    className={`main-content-wrapper ${isEffectivelyCollapsed ? 'sidebar-collapsed' : 'sidebar-resizable'}`}
                >
                    {/* New top nav — all pages */}
                    {(
                        <div style={{
                            width: '100%', height: '72px', flexShrink: 0,
                            background: 'var(--background-color, #EEE)',
                            display: 'flex', alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0 32px', boxSizing: 'border-box',
                            boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.45)' : '0 4px 16px rgba(0,0,0,0.05)',
                            position: 'relative', zIndex: 100
                        }}>
                            {/* Greeting */}
                            <div>
                                <p style={{
                                    margin: 0,
                                    color: 'var(--text-primary, #1A1A1A)',
                                    fontFamily: 'Poppins, sans-serif',
                                    fontSize: '17px', fontWeight: 500,
                                    lineHeight: '23px', letterSpacing: '-0.3px'
                                }}>
                                    {greeting}{fullName ? `, ${fullName}` : ''}
                                </p>
                                {(userRole || orgName) && (
                                    <p style={{
                                        margin: 0,
                                        color: 'var(--text-muted, #999)',
                                        fontFamily: 'Poppins, sans-serif',
                                        fontSize: '11px',
                                        fontStyle: 'normal',
                                        fontWeight: 500,
                                        lineHeight: '15px'
                                    }}>
                                        {[userRole, orgName].filter(Boolean).join('  ·  ')}
                                    </p>
                                )}
                            </div>

                            {/* Right actions container */}
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '16px' }}>

                            {/* Neumorphic toggle */}
                            <button
                                onClick={toggleDarkMode}
                                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    padding: 0, width: '60px', height: '24px',
                                    position: 'relative', flexShrink: 0
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="60" height="24" viewBox="0 0 60 24" fill="none" style={{ position: 'absolute', top: 0, left: 0 }}>
                                    <g filter="url(#nm-track)">
                                        <rect width="60" height="24" rx="12"
                                            fill={isDarkMode ? '#2A2A2A' : '#EEEEEE'}
                                        />
                                    </g>
                                    <defs>
                                        <filter id="nm-track" x="-2.39572" y="-2.39572" width="65.9893" height="29.9893" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                                            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                                            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                                            <feOffset dx="3.59358" dy="3.59358"/><feGaussianBlur stdDeviation="2.99465"/>
                                            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                                            <feColorMatrix type="matrix" values={isDarkMode
                                                ? "0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.7 0"
                                                : "0 0 0 0 0.531528 0 0 0 0 0.6278 0 0 0 0 0.716667 0 0 0 0.35 0"}/>
                                            <feBlend mode="normal" in2="shape" result="s1"/>
                                            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                                            <feOffset dx="-2.39572" dy="-2.39572"/><feGaussianBlur stdDeviation="2.99465"/>
                                            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                                            <feColorMatrix type="matrix" values={isDarkMode
                                                ? "0 0 0 0 0.2 0 0 0 0 0.2 0 0 0 0 0.2 0 0 0 0.08 0"
                                                : "0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.55 0"}/>
                                            <feBlend mode="normal" in2="s1" result="s2"/>
                                        </filter>
                                    </defs>
                                </svg>
                                {/* Ball — vertically centred, stays inside the 60×24 track */}
                                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none"
                                    style={{ position: 'absolute', top: '1px', left: isDarkMode ? '37px' : '1px', transition: 'left 0.25s ease' }}>
                                    <g filter="url(#nm-ball)"><rect x="2.39572" y="2.39575" width="18" height="18" rx="9"
                                        fill="#EEEEEE"
                                        style={{ fill: isDarkMode ? 'var(--primary-color, #9404C3)' : '#EEEEEE' }}
                                    /></g>
                                    <defs>
                                        <filter id="nm-ball" x="-2.38419e-07" y="3.02792e-05" width="29.9786" height="29.9786" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                                            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                                            <feOffset dx="3.59358" dy="3.59358"/><feGaussianBlur stdDeviation="2.99465"/>
                                            <feColorMatrix type="matrix" values={isDarkMode
                                                ? "0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.7 0"
                                                : "0 0 0 0 0.531528 0 0 0 0 0.6278 0 0 0 0 0.716667 0 0 0 0.35 0"}/>
                                            <feBlend mode="normal" in2="BackgroundImageFix" result="d1"/>
                                            <feBlend mode="normal" in="SourceGraphic" in2="d1" result="shape"/>
                                        </filter>
                                    </defs>
                                </svg>
                            </button>

                            {/* Notification bell */}
                            <button
                                onClick={() => setIsNotifOpen(o => !o)}
                                style={{
                                    width: '38px', height: '38px',
                                    borderRadius: '23.957px',
                                    border: `2.396px solid ${isDarkMode ? 'var(--surface-color, #252526)' : '#EEE'}`,
                                    background: isDarkMode ? 'var(--surface-color, #252526)' : '#EEE',
                                    boxShadow: isDarkMode
                                        ? '-2.4px -2.4px 6px 0 rgba(60,60,60,0.3) inset, 3.6px 3.6px 6px 0 rgba(0,0,0,0.5) inset'
                                        : '-2.396px -2.396px 5.989px 0 rgba(255,255,255,0.55) inset, 3.594px 3.594px 5.989px 0 rgba(136,160,183,0.35) inset',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer', flexShrink: 0,
                                    position: 'relative'
                                }}
                            >
                                <BellIcon style={{
                                    width: '24px', height: '24px',
                                    color: 'var(--primary-color, #9404C3)',
                                    fill: 'var(--primary-color, #9404C3)'
                                }} />
                                {unreadCount > 0 && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '-2px', right: '-2px',
                                        width: '12px', height: '12px',
                                        borderRadius: '9999px',
                                        background: 'var(--primary-color, #9404C3)',
                                        boxShadow: '0 0 8px 0 var(--shadowLight, rgba(148,4,195,0.40))',
                                        pointerEvents: 'none'
                                    }} />
                                )}
                            </button>

                            {/* Profile initials — capabilities page only */}
                            <div ref={profileDropdownRef} style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setIsProfileOpen(o => !o)}
                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                >
                                    <div style={{
                                        width: '38px', height: '38px',
                                        borderRadius: '23.957px',
                                        border: `2.396px solid ${isDarkMode ? 'var(--surface-color, #252526)' : '#EEE'}`,
                                        background: isDarkMode ? 'var(--surface-color, #252526)' : '#EEE',
                                        boxShadow: isDarkMode
                                            ? '-2.4px -2.4px 6px 0 rgba(60,60,60,0.3) inset, 3.6px 3.6px 6px 0 rgba(0,0,0,0.5) inset'
                                            : '-2.396px -2.396px 5.989px 0 rgba(255,255,255,0.55) inset, 3.594px 3.594px 5.989px 0 rgba(136,160,183,0.35) inset',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        <span style={{
                                            color: 'var(--primary-color, #9404C3)',
                                            fontFamily: 'Poppins, sans-serif',
                                            fontSize: '14px', fontWeight: 700,
                                            lineHeight: '20px', userSelect: 'none'
                                        }}>
                                            {initials}
                                        </span>
                                    </div>
                                </button>

                                {isProfileOpen && (
                                    <div style={{
                                        position: 'absolute', top: 'calc(100% + 10px)', right: 0,
                                        minWidth: '220px', background: 'var(--card-background, #fff)',
                                        borderRadius: '16px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                        border: '1px solid var(--border-color, rgba(0,0,0,0.08))',
                                        overflow: 'hidden', zIndex: 100000,
                                        animation: 'dropdown-fade-in 0.2s ease-out'
                                    }}>
                                        {/* Header */}
                                        <div style={{
                                            padding: '16px 20px',
                                            borderBottom: '1px solid var(--border-color, rgba(0,0,0,0.06))',
                                            background: 'var(--surface-color, #f8f9fa)'
                                        }}>
                                            <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: '15px', color: 'var(--text-primary, #1a1a1a)' }}>
                                                {fullName || userData?.name || ''}
                                            </p>
                                            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary, #6b7280)' }}>
                                                {userData?.email || ''}
                                            </p>
                                        </div>
                                        {/* Logout */}
                                        <div style={{ padding: '8px' }}>
                                            <button
                                                onClick={() => { setIsProfileOpen(false); logout(); }}
                                                style={{
                                                    width: '100%', padding: '10px 14px',
                                                    display: 'flex', alignItems: 'center', gap: '10px',
                                                    background: 'none', border: 'none', borderRadius: '10px',
                                                    cursor: 'pointer', fontSize: '14px', fontWeight: 500,
                                                    color: '#ef4444', textAlign: 'left',
                                                    transition: 'background 0.15s'
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.06)'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                            >
                                                <LogOut size={15} />
                                                Log out
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            </div>

                            {/* Notification panel */}
                            <NotificationPanel
                                isOpen={isNotifOpen}
                                onClose={() => setIsNotifOpen(false)}
                                notifications={notifications}
                                onMarkAsRead={handleMarkAsRead}
                                onMarkAllAsRead={handleMarkAllAsRead}
                                userId={userId}
                            />
                        </div>
                    )}

                    <div className="content-container">
                        <div className="main-content-card" ref={contentRef}>
                            {!hideBreadcrumbs && <Breadcrumbs pathname={location.pathname} onNavigate={navigate} projectName={currentProjectName} />}
                            <Outlet />
                        </div>
                        <GlobalRunProgress />
                    </div>
                </div>
            </div>
        </div>
    );
});

MainLayout.displayName = 'MainLayout';

export default MainLayout;